/**
 * Policies -> Audit Log: what the org's policies have actually stopped. The tab's URL slug is
 * still `decisions`, because the worker links terraform at it from a refused apply.
 *
 * TWO EXPORTS, BECAUSE THE READING SPLITS IN TWO.
 *
 * `PolicyDecisionsSummary` is the at-a-glance half - the stat strip, the refusal series and the
 * ranked policies - and it lives on the OVERVIEW tab, beside the per-artefact tiles whose `blocked`
 * counts it explains. Those three are window summaries that never depended on the ledger's filters,
 * so they belonged with the other summaries rather than pushing the table down the page.
 *
 * `PolicyDecisionsPanel` is the ledger itself, and it is now the whole of the Audit Log tab: the
 * table is the first thing on screen rather than the third. Picking a policy in the Overview's
 * ranked list switches tabs with that filter applied, which is the one correlation the two halves
 * still need between them.
 *
 * A TABLE, NOT A TIMELINE. The feed used to render through DecisionList, which wrapped every row in
 * the same vertical rail as the workspace Runs tab - so it looked sequential when these rows are
 * unrelated events across different workspaces, modules and providers. Columns are what let two
 * rows be compared, which is most of what a ledger is for. The rail stays where it belongs, on the
 * per-workspace timeline, where the entries really are one causal chain.
 *
 * NO CARD AROUND THE LEDGER. It is a page-width table with a filter bar, which is exactly the
 * Modules page, so it is built like the Modules page: a bordered `.table-wrap` and nothing outside
 * it. The card chrome drew a second rounded border a few pixels outside the table's own, and the
 * two borders never agreed with anything else in the app.
 *
 * WHAT THE SUMMARIES SCOPE TO. The strip and both cards describe the WINDOW, not the table: they
 * are on another tab now, so there are no filters in sight to imply otherwise.
 *
 * WHERE THE PROSE WENT. This file used to carry two multi-line caveat paragraphs that burned the
 * viewport on every visit. The caveats are real and are now in the HelpIcon of the card each one
 * belongs to, which is what HelpIcon is for.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { DecisionLedger, DecisionLedgerFilters, PolicyCoverageItem } from '@/lib/apiTypes'
import { useOrgDecisionLedger, useOrgPolicyCoverage, useOrgPolicySettings, useMediaQuery, useCopyToClipboard } from '@/lib/hooks'
import {
  decisionViews, topPoliciesByRefusal, policiesFired, neverEvaluatedCount,
  policyArtefactIndex, interactionLabel, interactionBucket, sourceLabel,
  VERDICT_FILTERS, VERDICT_FILTER_LABEL, VERDICT_FILTER_COLOR,
  INTERACTION_FILTERS, INTERACTION_FILTER_LABEL,
  type DecisionView, type RankedPolicy, type VerdictFilter, type InteractionFilter,
} from '@/features/policies/lib/decisionView'
import { SkeletonCard, SkeletonTable } from '@/shared/components/Skeleton'
import { SegmentedFilter } from '@/shared/components/SegmentedFilter'
import { PolicyKpiStrip } from '@/features/policies/components/PolicyKpiStrip'
import { PushCadenceChart } from '@/shared/components/PushCadenceChart'
import { EmptyState } from '@/shared/components/EmptyState'
import { HelpIcon } from '@/shared/components/HelpIcon'
import { Tooltip } from '@/shared/components/Tooltip'
import { formatCount, formatDateTimeSeconds, relativeTime } from '@/lib/format'
import s from '@/features/policies/components/PolicyDecisionsPanel.module.css'

const META: React.CSSProperties = { color: 'var(--text3)', fontSize: '0.7rem' }
const MONO_SM: React.CSSProperties = { fontFamily: 'var(--mono)', fontSize: '0.72rem' }

export interface LedgerFilterState {
  verdict: VerdictFilter
  interaction: InteractionFilter
  policyId: string | null
  q: string
  /** One request's refusal, arriving from the link a refused terraform/tofu run printed. */
  reqId: string | null
}

/**
 * The window summaries, for the Overview tab.
 *
 * It fetches the ledger UNFILTERED and one row deep: `facets` and `daily` are computed over the
 * whole 30-day window on the server whatever the page size, and nothing here renders a row.
 *
 * `onPickPolicy` is handed the ranked list's click. There is no table under it to filter here, so
 * the page owning this decides what a pick means - today it opens the Decisions tab already scoped
 * to that policy, which is what the click promised when the two sat on one tab.
 */
export function PolicyDecisionsSummary({ orgId, onPickPolicy }: {
  orgId: string
  onPickPolicy: (policyId: string) => void
}) {
  const narrow = useMediaQuery('(max-width: 900px)')
  const ledger = useOrgDecisionLedger(orgId, {}, 1)
  const coverage = useOrgPolicyCoverage(orgId)
  const settings = useOrgPolicySettings(orgId)

  const cov = coverage.data?.coverage ?? []
  const windowDays = ledger.windowDays
  const facets = ledger.facets

  const blocks = facets?.verdict.find((v) => v.key === 'block')?.n ?? 0
  const overrides = facets?.verdict.find((v) => v.key === 'override')?.n ?? 0

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <PolicyKpiStrip
        blocks={blocks}
        overrides={overrides}
        settings={settings.data ?? null}
        fired={policiesFired(cov)}
        windowDays={windowDays}
      />

      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 20 }}>
        <RefusalsCard ledger={ledger} windowDays={windowDays} />
        <TopPoliciesCard
          coverage={cov}
          loading={coverage.loading}
          windowDays={windowDays}
          onPick={onPickPolicy}
        />
      </div>
    </div>
  )
}

/** The ledger, and only the ledger: the whole of the Audit Log tab. */
export function PolicyDecisionsPanel({ orgId, filters, onFilters }: {
  orgId: string
  filters: LedgerFilterState
  onFilters: (next: Partial<LedgerFilterState>) => void
}) {
  const narrow = useMediaQuery('(max-width: 900px)')

  // policy + q go to the SERVER (a search must reach beyond page 1); verdict + interaction are
  // server-side too, so their facet counts describe the window rather than one loaded page.
  const query: DecisionLedgerFilters = {
    ...(filters.verdict !== 'all' ? { verdict: filters.verdict } : {}),
    ...(filters.interaction !== 'all' ? { interaction: filters.interaction } : {}),
    ...(filters.policyId ? { policyId: filters.policyId } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.reqId ? { reqId: filters.reqId } : {}),
  }
  const ledger = useOrgDecisionLedger(orgId, query)
  // Only for the per-policy artefact sub-label in the Policy column.
  const coverage = useOrgPolicyCoverage(orgId)

  return (
    <DecisionLedgerCard
      orgId={orgId}
      ledger={ledger}
      filters={filters}
      onFilters={onFilters}
      artefactOf={policyArtefactIndex(coverage.data?.coverage ?? [])}
      narrow={narrow}
    />
  )
}

// ── Card chrome ──────────────────────────────────────────────────────────────

function Card({ kicker, title, help, children }: {
  kicker: string; title: string; help: React.ReactNode; children: React.ReactNode
}) {
  return (
    <div className="card fu">
      <div className="card-header">
        <div>
          <div className="kicker">{kicker}</div>
          <h2 style={{ fontSize: '0.95rem' }}>{title}</h2>
        </div>
        {help}
      </div>
      {children}
    </div>
  )
}

// ── Refusals over time ───────────────────────────────────────────────────────

function RefusalsCard({ ledger, windowDays }: { ledger: DecisionLedger; windowDays: number }) {
  return (
    <Card
      kicker="// refusals over time"
      title={`Refusals · last ${windowDays} days`}
      help={
        <HelpIcon track="help_policy_refusals_daily" title="Refusals over time" kicker="// what this implies">
          <p>
            One point per <strong>UTC day</strong>, counting refusals only. An override is a human
            act on a different clock from the machine traffic, so mixing the two into one line would
            make a busy override day read as a bad policy day.
          </p>
          <p>
            The series is zero-filled, so a dip to the axis is a real gap rather than a missing
            reading — the line is never drawn across one.
          </p>
          <p>
            It follows the policy and search filters, so a click on a bar in <em>Top policies</em>
            narrows this chart and the ledger together. It deliberately ignores the verdict and
            interaction segments: this is a refusal series by definition.
          </p>
        </HelpIcon>
      }
    >
      <div style={{ padding: '16px 18px' }}>
        {ledger.loading
          ? <SkeletonCard header={false} rows={4} />
          : (
            <PushCadenceChart
              pushes={ledger.daily}
              windowDays={windowDays}
              subject="refusal"
              plural="refusals"
              emptySubtitle="Refusals are counted per UTC day from the moment the gate said no."
              // The panel's own strip already shows the window total two rows above this.
              showStats={false}
            />
          )}
      </div>
    </Card>
  )
}

// ── Top policies by refusal ──────────────────────────────────────────────────

function TopPoliciesCard({ coverage, loading, windowDays, onPick }: {
  coverage: PolicyCoverageItem[]
  loading: boolean
  windowDays: number
  onPick: (id: string) => void
}) {
  const ranked = useMemo(() => topPoliciesByRefusal(coverage), [coverage])
  const fired = policiesFired(coverage)
  const unevaluated = neverEvaluatedCount(coverage)

  return (
    <Card
      kicker="// ranked"
      title={`Top policies by refusal · last ${windowDays} days`}
      help={
        <HelpIcon track="help_policy_top_policies" title="Top policies by refusal" kicker="// what this implies">
          <p>
            Ranked by how many refusals each rule actually caused. A policy with <strong>zero</strong>{' '}
            refusals is not necessarily a broken policy — it may simply never have been evaluated
            against anything, which the footnote below counts separately.
          </p>
          <p>
            The coverage figures elsewhere on this page are <strong>current</strong> verdict counts,
            not a rate over time. A per-tenant pass-rate series cannot exist: the worker excludes the
            tenant id from every metric on purpose and the collector strips it again, so there is no
            per-org series to read. Current coverage is the honest substitute, and it costs no extra
            bookkeeping.
          </p>
        </HelpIcon>
      }
    >
      <div style={{ padding: '14px 16px' }}>
        {loading ? <SkeletonCard header={false} rows={4} /> : ranked.length === 0 ? (
          <p style={{ ...META, margin: 0, lineHeight: 1.5 }}>
            {fired.total === 0
              ? 'No policies are authored yet, so nothing can refuse anything.'
              : `None of these ${formatCount(fired.total)} policies has refused anything in the last ${windowDays} days.`}
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 2 }}>
            {ranked.map((r) => (
              <RankedRow key={r.policyId} r={r} onPick={() => onPick(r.policyId)} />
            ))}
          </div>
        )}

        {!loading && (fired.total > fired.fired || unevaluated > 0) && (
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)', marginTop: 10, lineHeight: 1.6 }}>
            {fired.total > fired.fired && (
              <div>// {formatCount(fired.total - fired.fired)} of {formatCount(fired.total)} policies have never refused anything</div>
            )}
            {/* The genuinely useful diagnostic: a rule that CANNOT fire looks identical to one
                that simply has not, until you separate them. */}
            {unevaluated > 0 && (
              <div>// {formatCount(unevaluated)} enabled {unevaluated === 1 ? 'policy has' : 'policies have'} never been evaluated against any artefact</div>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * A click is a NAVIGATION now, not a toggle: the ledger it filters is on the Audit Log tab. So the
 * row carries no pressed state - `aria-pressed` on a control that leaves the page would announce a
 * filter the reader can no longer see here - and the title says where it goes.
 */
function RankedRow({ r, onPick }: { r: RankedPolicy; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={s.rankRow}
      title={`Show this policy's refusals in the decision ledger`}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--text)' }}>{r.name}</span>
          <span style={{ ...META, fontFamily: 'var(--mono)', fontSize: '0.58rem' }}>{r.artefactType}</span>
          {r.statusBadge && (
            <span className={`badge ${r.statusBadge === 'disabled' ? 'badge-neutral' : 'badge-amber'} badge-still`} style={{ fontSize: '0.54rem' }}>
              {r.statusBadge}
            </span>
          )}
        </div>
        {/* A 4px track with the figure as the direct label - single measure, single hue. */}
        <div aria-hidden style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, marginTop: 5, overflow: 'hidden' }}>
          <div style={{ width: `${Math.max(r.pct, 2)}%`, height: '100%', background: 'var(--accent)' }} />
        </div>
        {r.neverEvaluated && (
          <div style={{ ...META, fontSize: '0.62rem', marginTop: 3 }}>
            refused on a manual block — never evaluated against an artefact
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.95rem', color: 'var(--red)' }}>{formatCount(r.blocks)}</div>
        {r.lastBlockAt && <div style={{ ...META, fontSize: '0.58rem' }}>{relativeTime(r.lastBlockAt)}</div>}
      </div>
    </button>
  )
}

// ── The ledger ───────────────────────────────────────────────────────────────

/**
 * `.table-wrap` is `overflow: hidden`, so a column that does not fit is CLIPPED rather than
 * scrollable - it would simply be unreachable. Reason is therefore dropped on a narrow viewport
 * rather than squeezed: it is the longest cell by far, and it is still one click away in the
 * expansion (and in the tooltip at full width), so nothing is actually lost.
 */
const headersFor = (narrow: boolean): string[] =>
  narrow
    ? ['', 'When', 'Verdict', 'What happened', 'Subject', 'Policy']
    : ['', 'When', 'Verdict', 'What happened', 'Subject', 'Policy', 'Reason']

const HEADER_HINT: Record<string, string> = {
  When: 'When the gate decided. Hover for the exact timestamp.',
  Verdict: 'Whether the interaction was refused, or a human lifted a refusal.',
  'What happened': 'What terraform or tofu was doing. The verdict is the column beside it.',
  Subject: 'The artefact the decision was about. Links to it where the coordinates resolve.',
  Policy: 'The rule that decided, or the authority when there is no policy row.',
  Reason: 'The message the client was given. Hover for the whole thing.',
}

function DecisionLedgerCard({ orgId, ledger, filters, onFilters, artefactOf, narrow }: {
  orgId: string
  ledger: DecisionLedger
  filters: LedgerFilterState
  onFilters: (next: Partial<LedgerFilterState>) => void
  artefactOf: Record<string, string>
  narrow: boolean
}) {
  const headers = headersFor(narrow)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const views = useMemo(() => decisionViews(ledger.rows, orgId), [ledger.rows, orgId])
  const facets = ledger.facets

  // Following the link out of a refused apply means the operator came to READ this, so open the
  // rows instead of making them click each one. Guarded by a ref rather than keyed on `views` so
  // a row they then collapse stays collapsed when the list re-renders.
  const autoExpanded = useRef<string | null>(null)
  useEffect(() => {
    if (!filters.reqId || views.length === 0 || autoExpanded.current === filters.reqId) return
    autoExpanded.current = filters.reqId
    setExpanded(new Set(views.map((v) => v.id)))
  }, [filters.reqId, views])

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const verdictCount = (k: VerdictFilter) =>
    k === 'all' ? (facets?.verdict.reduce((n, v) => n + v.n, 0) ?? 0)
      : (facets?.verdict.find((v) => v.key === k)?.n ?? 0)
  const interactionCount = (k: InteractionFilter) =>
    k === 'all' ? (facets?.interaction.reduce((n, v) => n + v.n, 0) ?? 0)
      : (facets?.interaction.filter((v) => v.key.startsWith(`${k}.`)).reduce((n, v) => n + v.n, 0) ?? 0)

  const filtered = filters.verdict !== 'all' || filters.interaction !== 'all'
    || filters.policyId || filters.q || filters.reqId
  const activePolicyName = filters.policyId
    ? views.find((v) => v.policyId === filters.policyId)?.policyLabel ?? 'selected policy'
    : null

  return (
    // No card. The Modules page's shape: a caption line, the filter bar, then the table inside its
    // own single border and nothing drawn outside it.
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
      }}>
        <span style={{
          fontFamily: 'var(--mono)', fontSize: '0.65rem',
          letterSpacing: '0.08em', color: 'var(--text3)',
        }}>
          // every refusal and override · last {ledger.windowDays} days
        </span>
        <HelpIcon track="help_policy_ledger" title="The decision ledger" kicker="// what this implies">
          <p>
            <strong>Only refusals and the human overrides that lift them are recorded.</strong> A
            policy that passes leaves no row at all — a successful evaluation is a metric, not a
            ledger entry, so a <code>terraform init</code> that is served costs nothing here. An
            empty ledger therefore means nothing was <em>stopped</em>, not that nothing was checked.
          </p>
          <p>
            This is the policy audit log, not the account one. The account audit log records who
            changed Terramantle and is limited to Team and above; this records what terraform asked
            for and what the gate answered, and is available on every tier — a blocked apply needs
            explaining whatever you pay.
          </p>
          <p>
            Rows survive the deletion of the policy that caused them, which is the history worth
            keeping. The request and trace identifiers in an expanded row are for quoting to support;
            they are not links.
          </p>
        </HelpIcon>
      </div>

      <div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <SegmentedFilter
            label="Filter decisions by verdict"
            value={filters.verdict}
            onChange={(v) => onFilters({ verdict: v })}
            emptyNoun="decisions"
            options={VERDICT_FILTERS.map((k) => ({
              key: k,
              label: VERDICT_FILTER_LABEL[k],
              count: verdictCount(k),
              ...(VERDICT_FILTER_COLOR[k] ? { color: VERDICT_FILTER_COLOR[k]! } : {}),
            }))}
          />
          <SegmentedFilter
            label="Filter decisions by interaction"
            value={filters.interaction}
            onChange={(v) => onFilters({ interaction: v })}
            emptyNoun="decisions"
            options={INTERACTION_FILTERS.map((k) => ({
              key: k,
              label: INTERACTION_FILTER_LABEL[k],
              count: interactionCount(k),
            }))}
          />
          <input
            className="input"
            style={{ width: 230, fontSize: '0.78rem' }}
            placeholder="Search subject, reason or policy"
            aria-label="Search decisions"
            value={filters.q}
            onChange={(e) => onFilters({ q: e.target.value })}
          />
          {activePolicyName && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onFilters({ policyId: null })}
              title="Stop filtering by this policy"
            >
              policy: {activePolicyName} ×
            </button>
          )}
          {/* The operator followed a link out of a refused apply. Say so plainly: without this the
              table looks like the whole ledger has collapsed to one or two rows. */}
          {filters.reqId && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onFilters({ reqId: null })}
              title="Show the whole ledger again"
            >
              one refusal: {filters.reqId} ×
            </button>
          )}
        </div>

        {ledger.loading ? <SkeletonTable headers={headers} firstColTwoLines={false} rows={6} />
          : ledger.error ? <p style={{ color: 'var(--red)', fontSize: '0.8rem', margin: 0 }}>{ledger.error}</p>
          : views.length === 0 ? (
            // Two different facts, two different sentences.
            filters.reqId
              ? <EmptyState icon="⌛" title="That refusal is no longer in the ledger." subtitle={`Decisions are kept for ${ledger.windowDays} days, and this link is older than that. Clear the filter to see what is still here.`} />
              : filtered
              ? <EmptyState icon="⬡" title="No decisions match these filters." subtitle="Widen the window of what you are looking for, or clear a filter." />
              : <EmptyState icon="✓" title="Nothing has been refused." subtitle="No policy has stopped an apply, a plan, or a download in this window. Passes are not recorded here, so this is the healthy state." />
          ) : (
            <>
              <div className="table-wrap fu">
                <table>
                  <thead>
                    <tr>
                      {headers.map((h, i) => (
                        <th key={h || `c${i}`}>
                          {h && (
                            <Tooltip text={HEADER_HINT[h] ?? h} placement="top">
                              <span tabIndex={0} className="cursor-help">{h}</span>
                            </Tooltip>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {views.map((v) => (
                      <DecisionRow
                        key={v.id}
                        v={v}
                        expanded={expanded.has(v.id)}
                        onToggle={() => toggle(v.id)}
                        activePolicyId={filters.policyId}
                        onPickPolicy={(id) => onFilters({ policyId: filters.policyId === id ? null : id })}
                        artefactOf={artefactOf}
                        columns={headers.length}
                        showReason={!narrow}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {ledger.moreError && (
                <p style={{ color: 'var(--red)', fontSize: '0.78rem', margin: '10px 0 0' }}>{ledger.moreError}</p>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                {ledger.hasMore && (
                  <button className="btn btn-ghost btn-sm" onClick={ledger.loadMore} disabled={ledger.loadingMore}>
                    {ledger.loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
                {/* The total is exact (it comes from the facet pass), so this is a real count and
                    not a guess from the page size. */}
                <span style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)' }}>
                  // showing {formatCount(views.length)} of {formatCount(facets?.total ?? views.length)}, newest first
                </span>
              </div>
            </>
          )}
      </div>
    </div>
  )
}

function DecisionRow({ v, expanded, onToggle, activePolicyId, onPickPolicy, artefactOf, columns, showReason }: {
  v: DecisionView
  expanded: boolean
  onToggle: () => void
  activePolicyId: string | null
  onPickPolicy: (id: string) => void
  artefactOf: Record<string, string>
  columns: number
  showReason: boolean
}) {
  return (
    <Fragment>
      <tr
        className="row-link"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => {
          // Guarded on the row itself, so Space inside the chevron or a subject link is not hijacked.
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault()
            onToggle()
          }
        }}
      >
        <td style={{ width: 24 }}>
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${v.interactionLabel} of ${v.subject.label}`}
            onClick={(e) => { e.stopPropagation(); onToggle() }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: '0.65rem', padding: 0 }}
          >
            {expanded ? '▾' : '▸'}
          </button>
        </td>
        <td style={{ ...MONO_SM, color: 'var(--text3)', whiteSpace: 'nowrap' }} title={new Date(v.at).toISOString()}>
          {relativeTime(v.at)}
        </td>
        <td>
          <span className={`badge ${v.badge.cls} badge-still`}>{v.badge.text}</span>
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>{v.interactionLabel}</td>
        <td>
          <div style={{ ...META, fontFamily: 'var(--mono)', fontSize: '0.58rem' }}>{v.subject.kindLabel}</div>
          {v.subject.href
            ? <Link to={v.subject.href} onClick={(e) => e.stopPropagation()} style={{ fontSize: '0.78rem' }}>{v.subject.label}</Link>
            : (
              <Tooltip text={v.subject.unlinkableReason ?? ''} placement="top">
                <span tabIndex={0} style={{ fontSize: '0.78rem', cursor: 'help', borderBottom: '1px dotted var(--border2)' }}>
                  {v.subject.label}
                </span>
              </Tooltip>
            )}
        </td>
        <td>
          {v.policyId ? (
            <button
              type="button"
              aria-pressed={activePolicyId === v.policyId}
              onClick={(e) => { e.stopPropagation(); onPickPolicy(v.policyId!) }}
              title={activePolicyId === v.policyId ? 'Stop filtering by this policy' : 'Filter the ledger to this policy'}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
                fontSize: '0.78rem',
                color: activePolicyId === v.policyId ? 'var(--accent)' : 'var(--text)',
              }}
            >
              {v.policyLabel}
            </button>
          ) : (
            // No policy row at all: naming the authority tells the reader which knob to turn.
            <span style={{ fontSize: '0.78rem', color: 'var(--text2)' }}>{v.policyLabel}</span>
          )}
          {/* The sub-label is suppressed when it would just repeat the line above it: with no
              policy row, policyLabel IS the authority ("admin action"), so "admin action /
              admin action" was saying one thing twice. */}
          {(v.sourceLabel !== v.policyLabel || (v.policyId && artefactOf[v.policyId])) && (
            <div style={{ ...META, fontFamily: 'var(--mono)', fontSize: '0.58rem' }}>
              {v.sourceLabel !== v.policyLabel ? v.sourceLabel : ''}
              {v.policyId && artefactOf[v.policyId]
                ? `${v.sourceLabel !== v.policyLabel ? ' · ' : ''}${artefactOf[v.policyId]}`
                : ''}
            </div>
          )}
        </td>
        {showReason && (
        <td>
          {v.reason
            ? (
              <Tooltip text={v.reason} placement="top" interactive maxWidth={420}>
                <span
                  tabIndex={0}
                  style={{ display: 'inline-block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom', fontSize: '0.76rem', color: 'var(--text2)', cursor: 'help' }}
                >
                  {v.reason}
                </span>
              </Tooltip>
            )
            : <span style={META}>—</span>}
        </td>
        )}
      </tr>

      {expanded && (
        <tr>
          <td colSpan={columns} style={{ background: 'var(--bg3)', borderTop: 'none', padding: '8px 12px 12px' }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                {v.reason ?? 'No reason was recorded with this decision.'}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: '0.72rem' }}>
                <Detail label="exact time" value={formatDateTimeSeconds(v.at)} />
                <Detail label="decided by" value={`${v.policyLabel} · ${v.sourceLabel}`} />
                {v.actor && <Detail label="actor" value={v.actorType ? `${v.actor} (${v.actorType})` : v.actor} />}
                {v.client && <Detail label="client" value={v.client} />}
                {/* The reason a client is missing is path-specific, so do not assert the
                    state-backend one on a module or provider row that simply has no tool. */}
                {!v.client && (
                  <Detail
                    label="client"
                    value={interactionBucket(v.raw.interaction) === 'state'
                      ? 'not sent — terraform and tofu both send Go-http-client/1.1 on the state backend'
                      : 'not recorded'}
                    muted
                  />
                )}
                {v.subject.key && <Detail label="coordinates" value={v.subject.key} mono />}
                {v.raw.workspace_id && <Detail label="workspace id" value={v.raw.workspace_id} mono />}
                {v.raw.plan_version_id && <Detail label="plan version" value={v.raw.plan_version_id} mono />}
                {v.raw.run_id && <Detail label="run id" value={v.raw.run_id} mono />}
                {/* No client IP: the ledger stores one, but the feed projection deliberately does
                    not return it. It adds nothing to "why was my apply refused" and would widen
                    the tenant-facing surface with a caller's address for no gain. */}
              </div>

              <SupportIds reqId={v.reqId} traceId={v.traceId} />
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

function Detail({ label, value, mono, muted }: { label: string; value: string; mono?: boolean; muted?: boolean }) {
  return (
    <>
      <span style={{ fontFamily: 'var(--mono)', color: 'var(--text3)', fontSize: '0.62rem', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{
        color: muted ? 'var(--text3)' : 'var(--text2)',
        ...(mono ? { fontFamily: 'var(--mono)', fontSize: '0.68rem' } : {}),
        overflowWrap: 'anywhere',
      }}>
        {value}
      </span>
    </>
  )
}

/**
 * The request and trace ids.
 *
 * NEVER a link. These are tenant-facing, and the Grafana that would resolve a trace is
 * operator-only - so they are copyable reference strings and the copy says exactly that. Do not
 * "improve" this into a deep link.
 */
function SupportIds({ reqId, traceId }: { reqId: string | null; traceId: string | null }) {
  const { copy, copied } = useCopyToClipboard()
  const ids = [
    ...(reqId ? [{ label: 'request', value: reqId }] : []),
    ...(traceId ? [{ label: 'trace', value: traceId }] : []),
  ]
  return (
    <div style={{ marginTop: 2, paddingTop: 6, borderTop: '1px solid var(--border2)' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)', letterSpacing: '0.08em' }}>
        SUPPORT REFERENCE
      </span>
      {ids.length === 0 ? (
        <p style={{ ...META, margin: '4px 0 0', lineHeight: 1.5 }}>
          This decision was recorded by a background scan, so there is no request to identify.
        </p>
      ) : (
        <>
          <p style={{ ...META, margin: '4px 0 6px', lineHeight: 1.5 }}>
            Quote these if you contact support — they identify this exact request in our logs. They
            are not links.
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {ids.map((i) => (
              <span key={i.label} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)' }}>{i.label}</span>
                <code style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', color: 'var(--text2)' }}>{i.value}</code>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: '0.6rem', padding: '1px 6px' }}
                  onClick={(e) => { e.stopPropagation(); copy(i.value) }}
                >
                  {copied ? 'copied' : 'copy'}
                </button>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export { interactionLabel, sourceLabel }
